import { useState, useEffect, useReducer, useCallback } from 'react';
import { Calculator, X, Delete, Copy, Check, History } from 'lucide-react';

const KEYS = [
  ['C', '±', '%', '÷'],
  ['7', '8', '9', '×'],
  ['4', '5', '6', '−'],
  ['1', '2', '3', '+'],
  ['0', '.', '⌫', '='],
];

const OPS = { '÷': '/', '×': '*', '−': '-', '+': '+' };
const OP_KEYS = ['÷', '×', '−', '+'];

function evaluateExpr(raw) {
  const clean = String(raw).replace(/[×÷−]/g, (m) => OPS[m]).replace(/[^0-9+\-*/.() ]/g, '');
  if (!clean.trim()) return null;
  try {
    // eslint-disable-next-line no-new-func
    const val = Function(`"use strict";return (${clean})`)();
    if (val === undefined || val === null || Number.isNaN(val) || !Number.isFinite(val)) return 'Error';
    return String(Math.round((val + Number.EPSILON) * 1e8) / 1e8);
  } catch {
    return 'Error';
  }
}

const INITIAL = { display: '0', expr: '', evaluated: false, history: [] };

function reducer(state, action) {
  const key = typeof action === 'string' ? action : action.key;
  const { display, expr, evaluated, history } = state;

  if (key === 'CLEAR_HISTORY') return { ...state, history: [] };
  if (key === 'USE_HISTORY') {
    return { ...state, display: action.value, expr: action.value, evaluated: true };
  }

  if (key === 'C') return { ...INITIAL, history };

  if (key === '⌫') {
    if (evaluated || display === 'Error') return { ...INITIAL, history };
    const next = display.length > 1 ? display.slice(0, -1) : '0';
    return { ...state, display: next, expr: expr.slice(0, -1), evaluated: false };
  }

  if (key === '±') {
    if (display === '0' || display === 'Error') return state;
    const flipped = display.startsWith('-') ? display.slice(1) : '-' + display;
    return { ...state, display: flipped, expr: flipped };
  }

  if (key === '%') {
    if (display === 'Error') return state;
    const src = expr || display;
    // find the last binary operator (+ − × ÷); leading negatives use ASCII '-', not counted
    let idx = -1;
    for (let i = src.length - 1; i >= 0; i--) {
      if (OP_KEYS.includes(src[i])) { idx = i; break; }
    }
    const cur = parseFloat(idx >= 0 ? src.slice(idx + 1) : src);
    if (Number.isNaN(cur)) return state;
    if (idx > 0) {
      const left = evaluateExpr(src.slice(0, idx));
      if (left !== null && left !== 'Error') {
        const op = src[idx];
        const base = parseFloat(left);
        // + / − : percent OF the left operand; × / ÷ : plain fraction
        const pctVal = (op === '+' || op === '−') ? (base * cur) / 100 : cur / 100;
        const pctStr = String(pctVal);
        return { ...state, display: pctStr, expr: src.slice(0, idx + 1) + pctStr, evaluated: false };
      }
    }
    // standalone: X% = X/100
    const solo = String(cur / 100);
    return { ...state, display: solo, expr: solo, evaluated: false };
  }

  if (key === '=') {
    const source = expr || display;
    const v = evaluateExpr(source);
    if (v === null) return state;
    const newHistory = v === 'Error'
      ? history
      : [{ expr: source, result: v }, ...history].slice(0, 8);
    return { ...state, display: v, expr: v === 'Error' ? '' : v, evaluated: true, history: newHistory };
  }

  if (OP_KEYS.includes(key)) {
    if (display === 'Error') return { ...state, display: '0', expr: key, evaluated: false };
    const base = expr || display;
    const trimmed = OP_KEYS.includes(base.slice(-1)) ? base.slice(0, -1) : base;
    return { ...state, display: key, expr: trimmed + key, evaluated: false };
  }

  // digit or dot
  if (key === '.' && display.includes('.') && !evaluated) return state;

  if (display === '0' || display === 'Error' || evaluated) {
    const startExpr = OP_KEYS.includes(expr.slice(-1)) ? expr : '';
    const v = key === '.' ? '0.' : key;
    return { ...state, display: v, expr: startExpr + v, evaluated: false };
  }

  return { ...state, display: display + key, expr: expr + key, evaluated: false };
}

export default function FloatingCalculator() {
  const [open, setOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [copied, setCopied] = useState(false);
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const press = useCallback((k) => dispatch(k), []);

  const copyResult = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(state.display);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* clipboard unavailable */ }
  }, [state.display]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      const k = e.key;
      if (k >= '0' && k <= '9') press(k);
      else if (k === '.') press('.');
      else if (k === '+') press('+');
      else if (k === '-') press('−');
      else if (k === '*') press('×');
      else if (k === '/') { e.preventDefault(); press('÷'); }
      else if (k === 'Enter' || k === '=') { e.preventDefault(); press('='); }
      else if (k === 'Backspace') press('⌫');
      else if (k === 'Escape') setOpen(false);
      else if (k === '%') press('%');
      else if (k.toLowerCase() === 'c') press('C');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, press]);

  return (
    <>
      <button
        data-testid="floating-calc-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Close calculator' : 'Open calculator'}
        className="fixed bottom-4 right-4 z-[2147483647] h-12 w-12 sm:w-auto sm:px-6 rounded-full flex items-center justify-center gap-2 bg-brand text-brand-ink shadow-xl font-semibold text-sm tracking-wide hover:-translate-y-0.5 transition-transform duration-200"
      >
        {open ? <><X size={20} strokeWidth={2.5} /> <span className="hidden sm:inline">Close</span></> : <><Calculator size={20} strokeWidth={2.5} /> <span className="hidden sm:inline">Calculator</span></>}
      </button>

      {open && (
        <div
          data-testid="floating-calc-panel"
          className="fixed bottom-20 right-4 z-[2147483647] w-[280px] rounded-2xl overflow-hidden bg-slate-900 border border-slate-800 shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-200"
        >
          <div className="flex items-center justify-between bg-brand px-3 py-2">
            <span className="font-display font-semibold text-sm uppercase tracking-tight text-slate-900">Calculator</span>
            <div className="flex items-center gap-1">
              <button
                data-testid="floating-calc-history-toggle"
                onClick={() => setShowHistory((s) => !s)}
                aria-label="Toggle history"
                className={`p-1 hover:opacity-70 ${showHistory ? 'text-white' : 'text-slate-900'}`}
              >
                <History size={17} strokeWidth={2.5} />
              </button>
              <button
                data-testid="floating-calc-copy"
                onClick={copyResult}
                aria-label="Copy result"
                className="p-1 text-slate-900 hover:opacity-70"
              >
                {copied ? <Check size={17} strokeWidth={2.5} /> : <Copy size={17} strokeWidth={2.5} />}
              </button>
              <button data-testid="floating-calc-close" onClick={() => setOpen(false)} aria-label="Close" className="p-1 text-slate-900 hover:opacity-70">
                <X size={18} strokeWidth={2.5} />
              </button>
            </div>
          </div>

          {showHistory && (
            <div className="bg-slate-950 border-b border-slate-700 max-h-40 overflow-y-auto" data-testid="floating-calc-history">
              <div className="flex items-center justify-between px-3 py-1.5 sticky top-0 bg-slate-950">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Tape</span>
                {state.history.length > 0 && (
                  <button
                    data-testid="floating-calc-history-clear"
                    onClick={() => dispatch('CLEAR_HISTORY')}
                    className="text-[10px] uppercase tracking-widest text-slate-500 hover:text-brand font-semibold"
                  >
                    Clear
                  </button>
                )}
              </div>
              {state.history.length === 0 ? (
                <p className="px-3 pb-2 font-mono text-xs text-slate-600">No calculations yet</p>
              ) : (
                <ul>
                  {state.history.map((h, i) => (
                    <li key={i}>
                      <button
                        data-testid={`floating-calc-history-item-${i}`}
                        onClick={() => dispatch({ key: 'USE_HISTORY', value: h.result })}
                        className="w-full text-right px-3 py-1 hover:bg-slate-800 transition-colors"
                        title="Tap to reuse this result"
                      >
                        <span className="font-mono text-[11px] text-slate-500 truncate block">{h.expr} =</span>
                        <span className="font-mono text-sm text-slate-200">{h.result}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="px-4 pt-4 pb-2 text-right">
            <div className="font-mono text-xs text-slate-500 h-4 truncate" data-testid="floating-calc-expr">{state.expr || '\u00A0'}</div>
            <div className="font-mono font-bold text-3xl text-white truncate" data-testid="floating-calc-display">{state.display}</div>
          </div>

          <div className="grid grid-cols-4 gap-px bg-slate-700 p-px">
            {KEYS.flat().map((k) => {
              const isOp = ['÷', '×', '−', '+', '='].includes(k);
              const isFn = ['C', '±', '%'].includes(k);
              return (
                <button
                  key={k}
                  data-testid={`floating-calc-key-${k}`}
                  onClick={() => press(k)}
                  className={[
                    'h-12 font-mono text-lg font-bold transition-colors flex items-center justify-center',
                    k === '=' ? 'bg-brand text-slate-900 hover:bg-[#ff7442]' :
                    isOp ? 'bg-slate-800 text-brand hover:bg-slate-700' :
                    isFn ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' :
                    'bg-slate-900 text-white hover:bg-slate-800',
                  ].join(' ')}
                >
                  {k === '⌫' ? <Delete size={18} /> : k}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
